import { useState } from 'react'
import { Control } from 'react-hook-form'
import { View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'

import FormFieldCheckbox from '@/components/form/FormFieldCheckbox'
import NewAdoptionAction from '@/components/fosters/actions/NewAdoptionAction'
import AdoptionListItem from '@/components/fosters/adoptions/AdoptionListItem'
import { FosterForm } from '@/components/fosters/fosterFormSchema'
import Card from '@/components/shared/Card'
import TourSpot from '@/components/tour/TourSpot'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import PaymentMethod from '@/enums/paymentMethod'
import useAppTour from '@/hooks/useAppTour'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import useUserRoles from '@/hooks/useUserRoles'
import FosterService from '@/services/fosterService'
import sharedStyles from '@/styles/styles'
import { AppTourKeys } from '../../../../constants'

interface Props {
  control: Control<FosterForm, any>
  disabled: boolean
  fosterId: string
}

const FosterAdoptionsCard = ({ control, disabled, fosterId }: Props) => {
  const { isNot } = useBreakpoint()
  const [newAdoptionRecordOpen, setNewAdoptionRecordOpen] = useState(false)
  const { isUserAdmin } = useUserRoles()

  const { foster } = FosterService.useFoster(fosterId)

  const hasAdoptions = !!foster?.adoptions?.length

  useAppTour({
    enabled: true,
    tourKey: AppTourKeys.FosterAdoptionTab,
  })

  return (
    <View>
      <Card title="Adoption Info">
        <TourSpot id="foster-adoption-tab">
          <View style={styles.togglesContainer}>
            <FormFieldCheckbox
              control={control}
              disabled={disabled}
              fieldName="readyForAdoption"
              label="Ready For Adoption"
              required={false}
              status="checked"
              style={styles.toggles}
            />
            {isUserAdmin && (
              <FormFieldCheckbox
                control={control}
                disabled={disabled}
                fieldName="isAdopted"
                label="Adopted"
                required={false}
                status="checked"
                style={styles.toggles}
              />
            )}
          </View>
        </TourSpot>

        {!foster?.adoptions.filter((adoption) => !adoption.returnDate)
          .length && (
          <View style={[sharedStyles.buttonRow, styles.buttonRowExtra]}>
            <UniButton
              disabled={disabled}
              mode="contained"
              onPress={() => setNewAdoptionRecordOpen(true)}
            >
              Add Adoption Info
            </UniButton>
          </View>
        )}
        {hasAdoptions && isNot('xs') && (
          <View style={styles.headerContainer}>
            <UniText style={[styles.nameColumnFlex, styles.headerColumn]}>
              Adopted By
            </UniText>
            <UniText style={[styles.columnFlex, styles.headerColumn]}>
              Pet's Adopted Name
            </UniText>
            <UniText style={[styles.columnFlex, styles.headerColumn]}>
              Adoption Date
            </UniText>
            <UniText style={[styles.columnFlex, styles.headerColumn]}>
              Returned On
            </UniText>
            <View style={styles.menuButtonContainer} />
          </View>
        )}
        {hasAdoptions &&
          foster?.adoptions.map((adoption, index) => {
            return (
              <AdoptionListItem
                adoption={{
                  ...adoption,
                  adoptedByPhone: adoption.adoptedByPhone || null,
                  paymentMethod: adoption.paymentMethod as PaymentMethod,
                }}
                disabled={disabled}
                fosterId={fosterId}
                fosterName={foster.name}
                key={`${adoption.updatedAt}-${adoption.id}`}
                testID={`adoption-list-item-${index}`}
              />
            )
          })}
        <NewAdoptionAction
          fosterId={fosterId}
          onClose={() => setNewAdoptionRecordOpen(false)}
          open={newAdoptionRecordOpen}
        />
      </Card>
    </View>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    buttonRowExtra: {
      marginBottom: theme.tokens.spacing[4],
    },
    columnFlex: {
      flex: {
        xs: 1,
        sm: 0.4,
      },
    },
    headerColumn: {
      color: theme.colors.primary,
      fontWeight: 'bold',
    },
    headerContainer: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      padding: theme.tokens.spacing[3],
    },
    menuButtonContainer: {
      flex: 0,
      minWidth: 40,
    },
    nameColumnFlex: {
      flex: {
        xs: 1,
        sm: 0.3,
      },
    },
    toggles: {
      flexBasis: {
        xs: '40%',
        md: '30%',
      },
      // flexGrow: 1,
    },
    togglesContainer: {
      flexDirection: {
        xs: 'column',
        md: 'row',
      },
      flex: 0.8,
    },
  }
})

export default FosterAdoptionsCard
